import { useRef, useState } from 'react'
import { Calendar, ArrowRight, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import {
  requestFlightChangeOptions,
  selectFlightChangeOffer,
  confirmFlightChange,
  type OrderChangeOffer,
} from '@/lib/bookings-api'
import { formatCurrency } from '@/lib/utils'
import type { TripFlightSlice } from '@/types/trip'

interface FlightChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  /** order_items row ID. The backend changes the booking of this item (VOY-567). */
  orderItemId: string
  slices?: TripFlightSlice[]
  currentOrigin?: string
  currentDestination?: string
  currentDepartureDate?: string
}

type Step = 'select-date' | 'loading-offers' | 'pick-offer' | 'confirming' | 'done'

export function FlightChangeModal({
  isOpen,
  onClose,
  onSuccess,
  orderItemId,
  slices,
  currentOrigin,
  currentDestination,
  currentDepartureDate,
}: FlightChangeModalProps) {
  const [step, setStep] = useState<Step>('select-date')
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [newDate, setNewDate] = useState(currentDepartureDate || '')
  const [offers, setOffers] = useState<OrderChangeOffer[]>([])
  const [selectedOffer, setSelectedOffer] = useState<OrderChangeOffer | null>(null)
  const busyRef = useRef(false)

  const resetState = () => {
    setStep('select-date')
    setError(null)
    setWarning(null)
    setNewDate(currentDepartureDate || '')
    setOffers([])
    setSelectedOffer(null)
    busyRef.current = false
  }

  const handleClose = () => {
    // Prevent closing during confirm — the Duffel change is being
    // committed and dismissing the dialog would leave the user without
    // feedback on whether it succeeded.
    if (step === 'confirming') return
    const wasSuccess = step === 'done'
    resetState()
    onClose()
    if (wasSuccess) onSuccess()
  }

  // The Duffel slice ID is an opaque identifier (e.g. sli_0000...) that
  // must come from the original order response via the backend trip API.
  // TripFlightSlice.id carries this when the backend populates it.
  // For MVP we change the first (outbound) slice.
  const firstSliceId = slices?.[0]?.id

  // ── Step 1: Request change offers ───────────────────────────────

  const handleSearchChanges = async () => {
    if (busyRef.current) return
    if (!newDate) {
      setError('Please select a new departure date.')
      return
    }
    if (newDate === currentDepartureDate) {
      setError('Please select a different date than the current departure.')
      return
    }
    if (!firstSliceId) {
      setError('Flight slice ID is not available. The backend may need to include Duffel slice IDs in the trip response.')
      return
    }
    busyRef.current = true
    setStep('loading-offers')
    setError(null)

    try {
      const result = await requestFlightChangeOptions(orderItemId, {
        remove: [{ slice_id: firstSliceId }],
        add: [{
          origin: currentOrigin || '',
          destination: currentDestination || '',
          departure_date: newDate,
        }],
      })

      if (result.orderChangeOffers.length === 0) {
        setError('No change options available for this date. Try a different date.')
        setStep('select-date')
      } else {
        setOffers(result.orderChangeOffers)
        setStep('pick-offer')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get change options')
      setStep('select-date')
    } finally {
      busyRef.current = false
    }
  }

  // ── Step 2 + 3: Select offer and confirm ────────────────────────

  const handleSelectAndConfirm = async (offer: OrderChangeOffer) => {
    if (busyRef.current) return
    busyRef.current = true
    setSelectedOffer(offer)
    setStep('confirming')
    setError(null)

    let selectSucceeded = false
    try {
      // Step 2: select the offer
      await selectFlightChangeOffer(orderItemId, offer.id)
      selectSucceeded = true

      // Step 3: confirm. VOY-567: the browser sends no payment. A change with a price
      // difference is refused by the backend until it is charged through Stripe.
      const result = await confirmFlightChange(orderItemId)
      if (result.warning) setWarning(result.warning)
      setStep('done')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm change'
      // If step 2 succeeded but step 3 failed, the change is in a
      // pending state at Duffel. Alert the user clearly so they know
      // their booking may be in an ambiguous state.
      if (selectSucceeded) {
        // The pending change exists at Duffel but wasn't confirmed.
        // Don't route back to pick-offer — the offers are stale and
        // re-selecting would create a second pending change. Route to
        // select-date so the user starts fresh, or contacts support.
        setError(`${message}. Your change request was submitted but confirmation failed. Please contact support or start a new change request.`)
        setOffers([])
        setStep('select-date')
      } else {
        setError(message)
        setStep('pick-offer')
      }
    } finally {
      busyRef.current = false
    }
  }

  // ── Render ──────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'done' ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Flight Changed
              </>
            ) : (
              <>
                <Calendar className="h-5 w-5 text-blue-600" />
                Change Flight
              </>
            )}
          </DialogTitle>
          {step !== 'done' && (
            <DialogDescription>
              {step === 'select-date' && 'Select a new departure date for your flight.'}
              {step === 'loading-offers' && 'Searching for available changes...'}
              {step === 'pick-offer' && 'Choose a change option below.'}
              {step === 'confirming' && 'Confirming your change...'}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Current flight info ── */}
          {step !== 'done' && currentOrigin && currentDestination && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
              <span className="font-medium">{currentOrigin}</span>
              <ArrowRight className="h-3 w-3" />
              <span className="font-medium">{currentDestination}</span>
              {currentDepartureDate && (
                <span className="ml-auto">{new Date(currentDepartureDate + 'T12:00:00').toLocaleDateString()}</span>
              )}
            </div>
          )}

          {/* ── Step: select-date ── */}
          {step === 'select-date' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="new-date">New departure date</Label>
                <Input
                  id="new-date"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* ── Step: loading ── */}
          {step === 'loading-offers' && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching for available changes...
            </div>
          )}

          {/* ── Step: pick-offer ── */}
          {step === 'pick-offer' && offers.length > 0 && (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {offers.map((offer) => {
                const changeAmount = parseFloat(offer.changeTotalAmount || '0')
                const penaltyAmount = parseFloat(offer.penaltyTotalAmount || '0')
                const isRefund = changeAmount < 0
                const isFree = changeAmount === 0 && penaltyAmount === 0
                // VOY-567: a change with a price difference cannot be paid online yet. The backend
                // refuses it, so it is shown but cannot be selected.
                const needsPayment = !(changeAmount <= 0)

                return (
                  <Card
                    key={offer.id}
                    className={needsPayment
                      ? 'p-4 opacity-60 cursor-not-allowed'
                      : 'p-4 cursor-pointer hover:border-primary transition-colors'}
                    aria-disabled={needsPayment}
                    onClick={needsPayment ? undefined : () => handleSelectAndConfirm(offer)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          New total: {formatCurrency(parseFloat(offer.newTotalAmount), offer.newTotalCurrency)}
                        </div>
                        {penaltyAmount > 0 && (
                          <div className="text-xs text-orange-600">
                            Airline change fee: {formatCurrency(penaltyAmount, offer.penaltyTotalCurrency)}
                          </div>
                        )}
                        {needsPayment && (
                          <div className="text-xs text-muted-foreground">
                            Not available online. Contact support to make this change.
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${
                          isRefund ? 'text-green-600' :
                          isFree ? 'text-muted-foreground' :
                          'text-orange-600'
                        }`}>
                          {isFree ? 'No extra cost' :
                           isRefund ? `Refund ${formatCurrency(Math.abs(changeAmount), offer.changeTotalCurrency)}` :
                           `+${formatCurrency(changeAmount, offer.changeTotalCurrency)}`}
                        </span>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          )}

          {/* ── Step: confirming ── */}
          {step === 'confirming' && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Confirming your change...
            </div>
          )}

          {/* ── Step: done ── */}
          {step === 'done' && (
            <>
              <p className="text-sm text-muted-foreground">
                Your flight has been changed successfully.
                {selectedOffer && parseFloat(selectedOffer.changeTotalAmount) < 0 && (
                  <> A refund of {formatCurrency(Math.abs(parseFloat(selectedOffer.changeTotalAmount)), selectedOffer.changeTotalCurrency)} will be processed.</>
                )}
              </p>
              {warning && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">{warning}</AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end pt-2">
                <Button onClick={handleClose}>
                  Done
                </Button>
              </div>
            </>
          )}

          {/* ── Error ── */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ── Buttons ── */}
          {step === 'select-date' && (
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSearchChanges}>
                Search Changes
              </Button>
            </div>
          )}

          {step === 'pick-offer' && (
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setStep('select-date')}>
                Change Date
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
